import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Eye, EyeOff, GripVertical, Pencil, Plus, RotateCcw, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND_DEFAULTS, assetUrl } from '../data';

const API = process.env.REACT_APP_BACKEND_URL || '';

const inputCls =
  'mt-1.5 w-full rounded-lg border border-line/10 bg-ink/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition focus:border-lime/70 focus:ring-2 focus:ring-lime/15';
const labelCls = 'font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400';
const btnPrimary =
  'inline-flex items-center gap-2 rounded-full bg-lime px-6 py-2.5 font-display text-xs font-bold uppercase tracking-widest text-ink transition-all hover:shadow-[0_0_28px_rgba(153,204,0,0.45)] disabled:opacity-50';
const btnGhost =
  'inline-flex items-center gap-1.5 rounded-full border border-line/10 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-slate-400 transition-all hover:border-lime/60 hover:text-lime disabled:opacity-50';
const card = 'rounded-2xl border border-line/10 bg-ink-2/60 p-6 md:p-8';

const errMsg = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

/** Shared loader for /api/admin/site */
function useAdminSite(auth, onUnauthorized) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    axios
      .get(`${API}/api/admin/site`, auth())
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.status === 401) onUnauthorized();
        else toast.error(errMsg(err, 'Could not load site content'));
      });
  }, [auth, onUnauthorized]);
  useEffect(() => {
    load();
  }, [load]);
  return [data, load];
}

/** Image upload button → returns a URL usable as logo (stored under /api/files/uploads/...) */
function ImageUpload({ auth, onUploaded, label = 'Upload image', testid }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error('Image too large (max 5 MB)');
    const fd = new FormData();
    fd.append('file', f);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/admin/brand-upload`, fd, auth());
      onUploaded(res.data.url);
    } catch (err) {
      toast.error(errMsg(err, 'Upload failed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className={`${btnGhost} cursor-pointer ${busy ? 'pointer-events-none opacity-50' : ''}`}>
      <Upload size={11} /> {busy ? 'Uploading…' : label}
      <input type="file" accept=".png,.jpg,.jpeg,.webp,.gif" className="hidden" onChange={pick} data-testid={testid} />
    </label>
  );
}

function LogoPreview({ src, dark = true, className = 'h-20' }) {
  return (
    <div
      className={`flex items-center justify-center rounded-xl border border-line/10 p-4 ${className} ${
        dark ? 'bg-[#0B1006]' : 'bg-white'
      }`}
    >
      {src ? (
        <img src={assetUrl(src)} alt="logo preview" className="max-h-full max-w-full object-contain" />
      ) : (
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">No logo</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Branding & logos
// ---------------------------------------------------------------------------

const LOGO_SLOTS = [
  { key: 'logo_header', title: 'Header logo (top-left)', hint: 'Shown in the navigation bar. Transparent PNG recommended.' },
  { key: 'logo_event', title: 'Event logo (hero)', hint: 'Main CXO100 event logo in the hero section.' },
  { key: 'logo_footer', title: 'Footer logo', hint: 'Shown at the top of the footer.' },
  { key: 'logo_bottom', title: 'Bottom centre logo', hint: 'Rendered in reverse (white) at the very bottom.' },
];

export function BrandingPanel({ auth, onUnauthorized }) {
  const [data, reload] = useAdminSite(auth, onUnauthorized);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) setForm({ ...BRAND_DEFAULTS, ...Object.fromEntries(Object.entries(data.settings || {}).filter(([, v]) => v !== '')) });
  }, [data]);

  if (!form) return <p className="p-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Loading…</p>;

  const save = async (patch) => {
    const next = { ...form, ...patch };
    setForm(next);
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/settings`, next, auth());
      toast.success('Saved — live on the website');
      reload();
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errMsg(err, 'Could not save settings'));
    } finally {
      setSaving(false);
    }
  };

  const agendaOn = String(form.agenda_visible) === '1';

  return (
    <div className="space-y-6" data-testid="admin-branding-panel">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {LOGO_SLOTS.map((s) => (
          <div key={s.key} className={card} data-testid={`brand-slot-${s.key}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lime">{s.title}</p>
            <p className="mt-1.5 text-xs text-slate-500">{s.hint}</p>
            <div className="mt-5">
              <LogoPreview src={form[s.key]} />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <ImageUpload auth={auth} testid={`brand-upload-${s.key}`} label="Upload new" onUploaded={(url) => save({ [s.key]: url })} />
              {form[s.key] !== BRAND_DEFAULTS[s.key] && (
                <button type="button" className={btnGhost} disabled={saving} onClick={() => save({ [s.key]: BRAND_DEFAULTS[s.key] })}>
                  <RotateCcw size={11} /> Reset to default
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className={`${card} flex flex-wrap items-center justify-between gap-4`} data-testid="agenda-visibility-card">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lime">Schedule &amp; Agenda on website</p>
          <p className="mt-1.5 text-xs text-slate-500">
            {agendaOn
              ? 'Agenda is LIVE — visitors see the agenda items from the Agenda tab.'
              : 'Hidden — visitors see a "Work in Progress" message.'}
          </p>
        </div>
        <button
          type="button"
          data-testid="agenda-visibility-toggle"
          disabled={saving}
          onClick={() => save({ agenda_visible: agendaOn ? '0' : '1' })}
          className={agendaOn ? btnPrimary : `${btnGhost} px-5 py-2.5 text-[10px]`}
        >
          {agendaOn ? <Eye size={13} /> : <EyeOff size={13} />} {agendaOn ? 'Visible — click to hide' : 'Hidden — click to publish'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic drag-sortable list helper
// ---------------------------------------------------------------------------

function useDragSort(rows, onReorder) {
  const [dragIdx, setDragIdx] = useState(null);
  const handlers = (i) => ({
    draggable: true,
    onDragStart: () => setDragIdx(i),
    onDragOver: (e) => e.preventDefault(),
    onDragEnd: () => setDragIdx(null),
    onDrop: () => {
      if (dragIdx === null || dragIdx === i) return setDragIdx(null);
      const next = [...rows];
      const [m] = next.splice(dragIdx, 1);
      next.splice(i, 0, m);
      setDragIdx(null);
      onReorder(next);
    },
  });
  return [dragIdx, handlers];
}

function ConfirmDelete({ onConfirm, testid }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3500);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      type="button"
      data-testid={armed ? `${testid}-confirm` : testid}
      onClick={() => (armed ? onConfirm() : setArmed(true))}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] transition-all ${
        armed ? 'border border-red-400/60 bg-red-400/15 text-red-300' : 'border border-line/10 text-slate-500 hover:border-red-400/60 hover:text-red-300'
      }`}
    >
      <Trash2 size={11} /> {armed ? 'Confirm?' : 'Delete'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Sponsors
// ---------------------------------------------------------------------------

const EMPTY_SPONSOR = { name: '', tier: '', logo_url: '', website: '' };

function SponsorForm({ auth, initial, tiers, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_SPONSOR);
  const [busy, setBusy] = useState(false);
  const editing = Boolean(initial?._key);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => setForm(initial || EMPTY_SPONSOR), [initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Sponsor name is required');
    if (!form.logo_url) return toast.error('Please upload the sponsor logo');
    setBusy(true);
    try {
      const body = { ...form, tier: form.tier.trim() || 'Partners' };
      if (editing) await axios.put(`${API}/api/admin/sponsors/${initial._key}`, body, auth());
      else await axios.post(`${API}/api/admin/sponsors`, body, auth());
      toast.success(editing ? 'Sponsor updated' : 'Sponsor added');
      setForm(EMPTY_SPONSOR);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err, 'Could not save sponsor'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className={`${card} border-lime/25`} data-testid="sponsor-form">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lime">{editing ? `Edit sponsor — ${initial.name}` : 'Add a sponsor'}</p>
      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-[180px_1fr]">
        <div>
          <LogoPreview src={form.logo_url} dark={false} className="h-28" />
          <div className="mt-3 flex justify-center">
            <ImageUpload auth={auth} testid="sponsor-logo-upload" label={form.logo_url ? 'Change logo' : 'Upload logo *'} onUploaded={(url) => setForm((f) => ({ ...f, logo_url: url }))} />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className={labelCls}>Sponsor name <span className="text-lime">*</span></span>
            <input data-testid="sponsor-name-input" value={form.name} onChange={set('name')} placeholder="Company name" className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Tier / Category</span>
            <input data-testid="sponsor-tier-input" list="sponsor-tiers" value={form.tier} onChange={set('tier')} placeholder="e.g. Gold Partners" className={inputCls} />
            <datalist id="sponsor-tiers">
              {tiers.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </label>
          <label className="block sm:col-span-2">
            <span className={labelCls}>Website (optional)</span>
            <input data-testid="sponsor-website-input" value={form.website || ''} onChange={set('website')} placeholder="https://company.com" className={inputCls} />
          </label>
          <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
            <button type="submit" disabled={busy} className={btnPrimary} data-testid="sponsor-save-button">
              {editing ? <Check size={14} /> : <Plus size={14} />} {busy ? 'Saving…' : editing ? 'Save changes' : 'Add sponsor'}
            </button>
            {editing && (
              <button type="button" onClick={onCancel} className={btnGhost}>
                <X size={11} /> Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}

export function SponsorsPanel({ auth, onUnauthorized }) {
  const [data, reload] = useAdminSite(auth, onUnauthorized);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (data) setRows(data.sponsors || []);
  }, [data]);

  const reorder = async (next) => {
    setRows(next);
    try {
      await axios.patch(`${API}/api/admin/sponsors/reorder`, { keys: next.map((r) => r._key) }, auth());
      toast.success('Order saved');
    } catch (err) {
      toast.error(errMsg(err, 'Could not save order'));
      reload();
    }
  };
  const [dragIdx, dragHandlers] = useDragSort(rows, reorder);

  const remove = async (key) => {
    try {
      await axios.delete(`${API}/api/admin/sponsors/${key}`, auth());
      toast.success('Sponsor removed');
      if (editing?._key === key) setEditing(null);
      reload();
    } catch (err) {
      toast.error(errMsg(err, 'Could not delete'));
    }
  };

  const tiers = [...new Set(rows.map((r) => r.tier))];

  return (
    <div className="space-y-6" data-testid="admin-sponsors-panel">
      <SponsorForm
        auth={auth}
        initial={editing}
        tiers={tiers}
        onCancel={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          reload();
        }}
      />
      {!data ? (
        <p className="p-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Loading…</p>
      ) : !rows.length ? (
        <p className="rounded-xl border border-line/10 bg-ink-2/50 p-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-500">No sponsors yet</p>
      ) : (
        <>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            {rows.length} sponsors · drag rows to reorder — tiers appear on the website in this order
          </p>
          <div className="overflow-hidden rounded-xl border border-line/10" data-testid="admin-table-sponsors">
            {rows.map((r, i) => (
              <div
                key={r._key}
                {...dragHandlers(i)}
                data-testid={`sponsor-row-${i}`}
                className={`flex items-center gap-4 border-b border-line/5 bg-ink-2/40 px-4 py-3 transition-colors last:border-0 hover:bg-lime/5 ${
                  dragIdx === i ? 'opacity-40' : ''
                } ${editing?._key === r._key ? 'bg-lime/10' : ''}`}
              >
                <GripVertical size={15} className="shrink-0 cursor-grab text-slate-600" />
                <div className="flex h-12 w-24 shrink-0 items-center justify-center rounded-md bg-white p-1.5">
                  <img src={assetUrl(r.logo_url)} alt={r.name} className="max-h-full max-w-full object-contain" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-100">{r.name}</p>
                  <p className="truncate font-mono text-[9.5px] uppercase tracking-[0.18em] text-lime/80">{r.tier}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    className={btnGhost}
                    data-testid={`sponsor-edit-${i}`}
                    onClick={() => {
                      setEditing(r);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    <Pencil size={11} /> Edit
                  </button>
                  <ConfirmDelete testid={`sponsor-delete-${i}`} onConfirm={() => remove(r._key)} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agenda
// ---------------------------------------------------------------------------

const AGENDA_KINDS = ['keynote', 'panel', 'fireside', 'roundtable', 'workshop', 'presentation', 'awards', 'session', 'networking', 'break'];
const AGENDA_CITIES = ['Both', 'New Delhi', 'Mumbai'];
const EMPTY_AGENDA = { city: 'Both', time_slot: '', title: '', kind: 'session', moderator: '', speakers: '' };

function AgendaForm({ auth, initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_AGENDA);
  const [busy, setBusy] = useState(false);
  const editing = Boolean(initial?._key);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => setForm(initial ? { ...EMPTY_AGENDA, ...initial, speakers: initial.speakers || '' } : EMPTY_AGENDA), [initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.time_slot.trim() || !form.title.trim()) return toast.error('Time and Title are required');
    setBusy(true);
    try {
      if (editing) await axios.put(`${API}/api/admin/agenda/${initial._key}`, form, auth());
      else await axios.post(`${API}/api/admin/agenda`, form, auth());
      toast.success(editing ? 'Agenda item updated' : 'Agenda item added');
      setForm((f) => ({ ...EMPTY_AGENDA, city: f.city }));
      onSaved();
    } catch (err) {
      toast.error(errMsg(err, 'Could not save agenda item'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className={`${card} border-lime/25`} data-testid="agenda-form">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lime">{editing ? 'Edit agenda item' : 'Add an agenda item'}</p>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <label className="block">
          <span className={labelCls}>City</span>
          <select data-testid="agenda-city-select" value={form.city} onChange={set('city')} className={`${inputCls} appearance-none`}>
            {AGENDA_CITIES.map((c) => (
              <option key={c} value={c} className="bg-ink">
                {c === 'Both' ? 'Both cities' : c}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={labelCls}>Time <span className="text-lime">*</span></span>
          <input data-testid="agenda-time-input" value={form.time_slot} onChange={set('time_slot')} placeholder="10:00 AM – 10:30 AM" className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>Session type</span>
          <select data-testid="agenda-kind-select" value={form.kind} onChange={set('kind')} className={`${inputCls} appearance-none capitalize`}>
            {AGENDA_KINDS.map((k) => (
              <option key={k} value={k} className="bg-ink">
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className="block sm:col-span-3">
          <span className={labelCls}>Title <span className="text-lime">*</span></span>
          <input data-testid="agenda-title-input" value={form.title} onChange={set('title')} placeholder="Session title" className={inputCls} />
        </label>
        <label className="block sm:col-span-3">
          <span className={labelCls}>Moderator (optional)</span>
          <input data-testid="agenda-moderator-input" value={form.moderator} onChange={set('moderator')} placeholder="Name, Designation, Company" className={inputCls} />
        </label>
        <label className="block sm:col-span-3">
          <span className={labelCls}>Speakers — one per line (optional)</span>
          <textarea
            data-testid="agenda-speakers-input"
            value={form.speakers}
            onChange={set('speakers')}
            placeholder={'Jane Doe, CISO, Acme Corp\nJohn Smith, CIO, Globex'}
            className={`${inputCls} min-h-[100px] resize-y`}
          />
        </label>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button type="submit" disabled={busy} className={btnPrimary} data-testid="agenda-save-button">
          {editing ? <Check size={14} /> : <Plus size={14} />} {busy ? 'Saving…' : editing ? 'Save changes' : 'Add item'}
        </button>
        {editing && (
          <button type="button" onClick={onCancel} className={btnGhost}>
            <X size={11} /> Cancel
          </button>
        )}
      </div>
    </form>
  );
}

export function AgendaPanel({ auth, onUnauthorized }) {
  const [data, reload] = useAdminSite(auth, onUnauthorized);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    if (data) setRows(data.agenda || []);
  }, [data]);

  const reorder = async (next) => {
    setRows(next);
    try {
      await axios.patch(`${API}/api/admin/agenda/reorder`, { keys: next.map((r) => r._key) }, auth());
      toast.success('Order saved');
    } catch (err) {
      toast.error(errMsg(err, 'Could not save order'));
      reload();
    }
  };
  const [dragIdx, dragHandlers] = useDragSort(rows, reorder);

  const remove = async (key) => {
    try {
      await axios.delete(`${API}/api/admin/agenda/${key}`, auth());
      toast.success('Agenda item removed');
      if (editing?._key === key) setEditing(null);
      reload();
    } catch (err) {
      toast.error(errMsg(err, 'Could not delete'));
    }
  };

  const visible = String(data?.settings?.agenda_visible) === '1';

  return (
    <div className="space-y-6" data-testid="admin-agenda-panel">
      {data && (
        <div
          className={`rounded-xl border px-5 py-3 text-xs ${
            visible ? 'border-lime/40 bg-lime/10 text-lime' : 'border-amber-400/40 bg-amber-400/10 text-amber-200'
          }`}
        >
          {visible
            ? 'Agenda is LIVE on the website.'
            : 'Agenda is currently HIDDEN on the website ("Work in Progress" is shown). Publish it from the Branding tab when ready.'}
        </div>
      )}
      <AgendaForm
        auth={auth}
        initial={editing}
        onCancel={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          reload();
        }}
      />
      {!data ? (
        <p className="p-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Loading…</p>
      ) : !rows.length ? (
        <p className="rounded-xl border border-line/10 bg-ink-2/50 p-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
          No agenda items yet
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Drag rows to reorder (with “All” filter)</p>
            <div className="flex gap-2">
              {['All', ...AGENDA_CITIES].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFilter(c)}
                  className={`rounded-full px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] ${
                    filter === c ? 'bg-lime text-ink' : 'border border-line/10 text-slate-400 hover:text-lime'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-hidden rounded-xl border border-line/10" data-testid="admin-table-agenda">
            {rows.map((r, i) =>
              filter !== 'All' && r.city !== filter ? null : (
                <div
                  key={r._key}
                  {...(filter === 'All' ? dragHandlers(i) : {})}
                  data-testid={`agenda-row-${i}`}
                  className={`flex items-start gap-4 border-b border-line/5 bg-ink-2/40 px-4 py-4 transition-colors last:border-0 hover:bg-lime/5 ${
                    dragIdx === i ? 'opacity-40' : ''
                  } ${editing?._key === r._key ? 'bg-lime/10' : ''}`}
                >
                  <GripVertical size={15} className={`mt-1 shrink-0 text-slate-600 ${filter === 'All' ? 'cursor-grab' : 'opacity-30'}`} />
                  <div className="w-36 shrink-0">
                    <p className="font-mono text-[11px] text-lime">{r.time_slot}</p>
                    <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">{r.city === 'Both' ? 'Both cities' : r.city}</p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-100">{r.title}</p>
                    <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400">
                      {r.kind}
                      {r.moderator ? ` · Moderator: ${r.moderator}` : ''}
                    </p>
                    {r.speakers && (
                      <p className="mt-1 line-clamp-2 text-xs text-slate-500">{String(r.speakers).split('\n').filter(Boolean).join(' · ')}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      className={btnGhost}
                      data-testid={`agenda-edit-${i}`}
                      onClick={() => {
                        setEditing(r);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      <Pencil size={11} /> Edit
                    </button>
                    <ConfirmDelete testid={`agenda-delete-${i}`} onConfirm={() => remove(r._key)} />
                  </div>
                </div>
              )
            )}
          </div>
        </>
      )}
    </div>
  );
}
